import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { formatDate } from '../utils/formatDate';
import { Badge } from './UI';

export interface PedigreeNode {
  dogId: number | null;
  name: string;
  sex: string | null;
  dateOfBirth: string | null;
  photoUrl: string | null;
  isLinked: boolean;
  sire?: PedigreeNode | null;
  dam?: PedigreeNode | null;
}

// Hoehe/Abstand eines Blatt-Knotens (letzte Generation) - bestimmt die Gesamthoehe des Baums,
// jede vorherige Generation teilt sich diesen Platz per flex-1 rekursiv zu gleichen Teilen auf.
const LEAF_HEIGHT = 56;
const GAP = 12;

const PedigreeNodeCard: React.FC<{ node: PedigreeNode }> = ({ node }) => {
  const { t } = useTranslation();

  const card = (
    <div
      className={clsx(
        'w-full rounded-xl border bg-dark-800/60 px-2.5 py-1.5 text-xs transition-colors',
        node.isLinked && 'hover:border-primary-500/60 hover:bg-dark-800',
        node.sex === 'Male' && 'border-secondary-500/40',
        node.sex === 'Female' && 'border-primary-500/40',
        (!node.sex || node.sex === 'Unknown') && 'border-dark-600'
      )}
    >
      <div className="flex items-center gap-1.5">
        <span className="truncate font-medium text-dark-50">{node.name}</span>
        {node.sex === 'Male' && <Badge variant="secondary" size="sm">{t('dogs:sex.male')}</Badge>}
        {node.sex === 'Female' && <Badge variant="primary" size="sm">{t('dogs:sex.female')}</Badge>}
      </div>
      {node.dateOfBirth && <div className="text-dark-400">{formatDate(node.dateOfBirth, { year: 'numeric' })}</div>}
      {!node.isLinked && <div className="mt-0.5 text-[10px] text-dark-500">{t('pedigree:unlinkedAncestor')}</div>}
    </div>
  );

  return node.isLinked && node.dogId ? (
    <Link to={`/dogs/${node.dogId}`} className="block">
      {card}
    </Link>
  ) : (
    card
  );
};

const PedigreeEmptyCard: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="w-full rounded-xl border border-dashed border-dark-700/70 px-2.5 py-1.5 text-xs text-dark-600">
      {t('pedigree:emptyAncestor')}
    </div>
  );
};

const PedigreeNodeBox: React.FC<{ node: PedigreeNode | null | undefined; generations: number }> = ({ node, generations }) => (
  <div className="flex h-full items-stretch gap-3">
    <div className="flex h-full w-36 shrink-0 items-center sm:w-44">
      {node ? <PedigreeNodeCard node={node} /> : <PedigreeEmptyCard />}
    </div>
    {generations > 1 && (
      <div className="flex h-full flex-1 flex-col gap-3">
        <div className="flex-1">
          <PedigreeNodeBox node={node?.sire} generations={generations - 1} />
        </div>
        <div className="flex-1">
          <PedigreeNodeBox node={node?.dam} generations={generations - 1} />
        </div>
      </div>
    )}
  </div>
);

/**
 * Klassische Ahnentafel-Darstellung: der Ausgangshund links, seine Vorfahren generationsweise
 * nach rechts auffaechernd (oben Vater-Linie, unten Mutter-Linie). Horizontal scrollbar, da die
 * Breite mit der Generationenzahl waechst.
 */
export const PedigreeTree: React.FC<{ root: PedigreeNode; generations: number }> = ({ root, generations }) => {
  const leafCount = 2 ** (generations - 1);
  const height = leafCount * LEAF_HEIGHT + (leafCount - 1) * GAP;

  return (
    <div className="overflow-x-auto pb-2">
      <div style={{ height, minWidth: generations * 176 }} className="inline-flex w-full">
        <PedigreeNodeBox node={root} generations={generations} />
      </div>
    </div>
  );
};
